import React, { useState } from 'react';
import { authApi } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import { Lock, Save, User as UserIcon, Shield, Mail, Key } from 'lucide-react';
import { getGlobalRoleLabel } from '../utils/roles';

const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setMessage({ type: 'error', text: '비밀번호 확인이 일치하지 않습니다.' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await authApi.changePassword({ password });
      if (response.data.isSuccess) {
        setMessage({ type: 'success', text: '비밀번호가 성공적으로 변경되었습니다.' });
        setPassword('');
        setConfirmPassword('');
        await refreshUser();
      } else {
        setMessage({ type: 'error', text: response.data.message });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.message || '변경 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fade-in profile-page">
      <div style={{ marginBottom: '2.5rem' }}>
        <h1 style={{ fontSize: '1.55rem', fontWeight: '700' }}>내 정보 설정</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem' }}>계정의 기본 정보 확인 및 보안 설정을 관리합니다.</p>
      </div>

      <div className="profile-layout">
        {/* Account Info Card */}
        <div className="card shadow profile-card" style={{ padding: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#eff6ff', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UserIcon size={20} />
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '800' }}>계정 프로필</h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.25rem', background: '#f8fafc', borderRadius: '1rem' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '20px', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: '800', color: 'var(--primary-color)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                {user?.name?.charAt(0)}
              </div>
              <div>
                <div style={{ fontWeight: '800', fontSize: '1.25rem' }}>{user?.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  <Mail size={14} /> {user?.email}
                </div>
              </div>
            </div>

            <div style={{ padding: '1.25rem', border: '1px solid var(--border-color)', borderRadius: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <Shield size={14} /> 부여된 전역 권한
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span className={`badge ${user?.globalRole === 'ROOT_ADMIN' ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: '0.875rem', padding: '0.4rem 1rem' }}>
                  {getGlobalRoleLabel(user?.globalRole)}
                </span>
                {user?.globalRole === 'ROOT_ADMIN' && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                    관리 중인 서비스와 워크스페이스에 대한 전체 권한이 있습니다.
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Password Change Card */}
        <div className="card shadow profile-card" style={{ padding: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#fff7ed', color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Key size={20} />
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '800' }}>보안 및 비밀번호</h2>
          </div>

          <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <label className="label" style={{ marginBottom: '0.5rem' }}>새 비밀번호</label>
              <div style={{ position: 'relative' }}>
                <Lock size={16} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input 
                  type="password" 
                  className="input" 
                  style={{ paddingLeft: '2.75rem' }}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="영문, 숫자 포함 8자 이상 권장"
                  required
                />
              </div>
            </div>
            <div>
              <label className="label" style={{ marginBottom: '0.5rem' }}>비밀번호 확인</label>
              <div style={{ position: 'relative' }}>
                <Lock size={16} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input 
                  type="password" 
                  className="input" 
                  style={{ paddingLeft: '2.75rem' }}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="새 비밀번호를 한 번 더 입력하세요"
                  required
                />
              </div>
            </div>

            {message.text && (
              <div className="fade-in" style={{ 
                padding: '1rem', 
                borderRadius: '0.75rem', 
                fontSize: '0.875rem',
                fontWeight: '600',
                backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
                color: message.type === 'success' ? '#15803d' : '#b91c1c',
                border: `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`
              }}>
                {message.text}
              </div>
            )}

            <button 
              type="submit" 
              className="btn btn-primary" 
              style={{ width: '100%', padding: '1rem', marginTop: '0.5rem' }}
              disabled={loading}
            >
              <Save size={18} />
              <span>{loading ? '변경 처리 중...' : '비밀번호 업데이트'}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
